import {
  Spinner,
  useSnackbar,
  Icon,
  Tooltip,
  Select,
  Flag,
} from "@mindlercare/ui-web";
import { FeedbackDraft } from "api/types";
import {
  useGenerateFeedbackDraft,
  useGetFeedbackDraftStatus,
} from "api/useIcbtQueries";
import { LanguageCodeISO2, MarketEnum } from "@mindlercare/schema";
import { isAxiosError } from "axios";
import ConfirmButtonWithDialog from "components/lib/confirmButtonWithDialog";
import { logger } from "core/logger";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";
import { Typography } from "components/typography";

const DraftPanel = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.SPACING.SM};
  flex-shrink: 0;
`;

// A single pill-shaped split button: the "Generate AI draft" action on the
// left and a language (flag) selector on the right, separated by a divider.
const Pill = styled.div`
  display: inline-flex;
  align-items: center;
  height: 33px;
  background-color: ${({ theme }) => theme.COLORS.WHITE};
  border: 1px solid ${({ theme }) => theme.COLORS.GRAY_MEDIUM};
  border-radius: 70px;
  overflow: hidden;
  flex-shrink: 0;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.SPACING.XS};
  height: 100%;
  padding: 0 ${({ theme }) => theme.SPACING.SM} 0
    ${({ theme }) => theme.SPACING.MD};
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.COLORS.GRAY_BLACK};
  cursor: pointer;

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.COLORS.GRAY_LIGHT};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

// The Tooltip wraps its children in a plain block-level div; stretch it to the
// full pill height so the ActionButton (and its hover fill) covers the whole
// generate area rather than just the text height.
const ActionTooltip = styled(Tooltip)`
  align-self: stretch;
  display: flex;
`;

const PillDivider = styled.div`
  width: 1px;
  align-self: stretch;
  background-color: ${({ theme }) => theme.COLORS.GRAY_MEDIUM};
`;

// Flag + language code, vertically centered as a single inline unit.
const LanguageLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.SPACING.XS};
  line-height: 1;
  color: ${({ theme }) => theme.COLORS.GRAY_BLACK};
`;

// Strip the default Select trigger chrome so it sits flush inside the pill,
// keeping only the built-in expand chevron.
const LanguageTrigger = styled(Select.Trigger)`
  && {
    width: auto;
    height: 33px;
    gap: ${({ theme }) => theme.SPACING.XS};
    padding: 0 14px 0 10px;
    border: none;
    border-radius: 0;
    background: transparent;
  }

  &&:hover:not(:disabled),
  &&:focus:not(:disabled) {
    border: none;
    background: transparent;
  }

  /* Center the flag/text content so it lines up with the expand chevron. */
  && > span {
    display: flex;
    align-items: center;
  }
`;

const SmallSpinner = styled(Spinner).attrs({ size: 20 })``;

// Prompt names end with the language code, e.g. "feedback-se-en".
const draftLanguage = (
  draft: FeedbackDraft | null,
): LanguageCodeISO2 | null => {
  const code = draft?.promptName?.split("-").pop()?.toUpperCase();
  return code === "SV" || code === "EN" ? code : null;
};

interface GenerateDraftProps {
  programId: string;
  market: MarketEnum;
  defaultLanguage: LanguageCodeISO2;
  icbtRequestId: number;
  patientUserId: string;
  feedbackInput: string;
  previousDrafts?: FeedbackDraft[];
  disabled: boolean;
  fetchDrafts: () => Promise<FeedbackDraft[]>;
  onNewDraft: (draftText: string) => void;
  onRegenerating: (isRegenerating: boolean) => void;
}

export const GenerateDraft = ({
  programId,
  market,
  icbtRequestId,
  patientUserId,
  feedbackInput,
  previousDrafts,
  disabled,
  defaultLanguage,
  fetchDrafts,
  onNewDraft,
  onRegenerating,
}: GenerateDraftProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [selectedLanguage, setLanguage] = useState<LanguageCodeISO2 | null>(
    null,
  );
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);
  const [requestedDraftId, setRequestedDraftId] = useState<string | null>(null);
  const handledDraftIdRef = useRef<string | null>(null);

  const latestCompletedDraft =
    previousDrafts?.find((draft) => draft.status === "completed") ?? null;
  // Until the psychologist picks a language, follow the latest draft, pending
  // ones included, so a reopened modal shows the language being generated.
  const language =
    selectedLanguage ??
    draftLanguage(previousDrafts?.[0] ?? null) ??
    defaultLanguage;

  const { mutateAsync: generateDraft, isPending: isQueueing } =
    useGenerateFeedbackDraft();

  // Track the draft we just requested, or resume one that was still
  // generating when the modal was closed, so the psychologist does not have
  // to start over. The backend reports a pending draft older than the worker
  // timeout as failed, so polling always ends.
  const latestDraft = previousDrafts?.[0] ?? null;
  const activeDraftId =
    requestedDraftId ??
    (latestDraft?.status === "pending" ? latestDraft.id : null);

  const {
    data: draftStatus,
    isError: isStatusError,
    error: statusError,
  } = useGetFeedbackDraftStatus(activeDraftId);

  const isRegenerating =
    isQueueing ||
    (activeDraftId !== null &&
      !isStatusError &&
      (draftStatus === undefined || draftStatus.status === "pending"));

  useEffect(() => {
    onRegenerating(isRegenerating);
  }, [isRegenerating, onRegenerating]);

  const showGenerationError = useCallback(
    (reason: string) => {
      logger.error("Draft generation failed", {
        patientUserId,
        icbtRequestId,
        programId,
        error: reason,
      });
      enqueueSnackbar({
        text: t("LLM_AI_FEEDBACK.GENERATE_DRAFT_ERROR"),
        isError: true,
      });
    },
    [enqueueSnackbar, t, patientUserId, icbtRequestId, programId],
  );

  const applyDraftText = useCallback(
    (draftText: string | null | undefined) => {
      if (!draftText || draftText.includes("{{PSYCHOLOGIST_NAME}}")) {
        showGenerationError("completed draft is empty or has placeholders");
        return;
      }
      onNewDraft(draftText);
    },
    [onNewDraft, showGenerationError],
  );

  const applyCompletedDraft = useCallback(
    async (draftId: string) => {
      const drafts = await fetchDrafts();
      applyDraftText(drafts.find((draft) => draft.id === draftId)?.draftText);
    },
    [fetchDrafts, applyDraftText],
  );

  // Handle each draft's terminal status exactly once.
  useEffect(() => {
    if (activeDraftId === null || handledDraftIdRef.current === activeDraftId)
      return;

    if (isStatusError) {
      handledDraftIdRef.current = activeDraftId;
      showGenerationError(
        statusError instanceof Error ? statusError.message : "status failed",
      );
      return;
    }

    if (draftStatus?.id !== activeDraftId || draftStatus.status === "pending")
      return;

    handledDraftIdRef.current = activeDraftId;
    if (draftStatus.status === "completed") {
      applyCompletedDraft(draftStatus.id);
    } else {
      showGenerationError(draftStatus.errorReason ?? "failed");
      fetchDrafts();
    }
  }, [
    activeDraftId,
    draftStatus,
    isStatusError,
    statusError,
    applyCompletedDraft,
    showGenerationError,
    fetchDrafts,
  ]);

  const regenerateDraft = useCallback(async () => {
    try {
      const response = await generateDraft({
        programId,
        market,
        language,
        icbtRequestId,
        userId: patientUserId,
      });
      if (response.draftId) {
        setRequestedDraftId(response.draftId);
      } else {
        // Legacy synchronous backend: the text comes back directly.
        applyDraftText(response.draftText);
        fetchDrafts();
      }
    } catch (originalError) {
      const error =
        originalError instanceof Error
          ? originalError
          : new Error(String(originalError));
      logger.error("Draft generation request failed", {
        patientUserId,
        icbtRequestId,
        programId,
        error: error.message || String(error),
      });

      const isTooManyDrafts =
        isAxiosError(originalError) &&
        originalError.response?.data?.error?.message ===
          "TOO_MANY_DRAFTS_FOR_ICBT";

      enqueueSnackbar({
        text: t(
          isTooManyDrafts
            ? "LLM_AI_FEEDBACK.GENERATE_DRAFT_TOO_MANY"
            : "LLM_AI_FEEDBACK.GENERATE_DRAFT_ERROR",
        ),
        isError: true,
      });
    }
  }, [
    generateDraft,
    programId,
    market,
    language,
    icbtRequestId,
    patientUserId,
    applyDraftText,
    fetchDrafts,
    enqueueSnackbar,
    t,
  ]);

  const handleGenerateDraft = useCallback(
    (hasManualEdits: boolean) => {
      if (hasManualEdits) {
        setShowRegenerateConfirm(true);
        return;
      }

      regenerateDraft();
    },
    [regenerateDraft],
  );

  const handleRegenerateConfirm = useCallback(
    (confirmed: boolean) => {
      setShowRegenerateConfirm(false);
      if (confirmed) {
        regenerateDraft();
      }
    },
    [regenerateDraft],
  );

  const isTextLlmGenerated =
    !!latestCompletedDraft?.draftText &&
    feedbackInput === latestCompletedDraft.draftText;
  const hasManualEdits = feedbackInput !== "" && !isTextLlmGenerated;
  const showLastAttemptFailed =
    !isRegenerating && latestDraft?.status === "failed";

  return (
    <DraftPanel>
      {showLastAttemptFailed && (
        <Typography variant="body-small" color="ERROR">
          {t("LLM_AI_FEEDBACK.GENERATE_DRAFT_ERROR")}
        </Typography>
      )}
      <Pill>
        <ActionTooltip
          content={t("LLM_AI_FEEDBACK.GENERATE_DRAFT_BUTTON_TOOLTIP")}
          placement="bottom-end"
          maxWidth={182}
        >
          <ActionButton
            type="button"
            onClick={() => handleGenerateDraft(hasManualEdits)}
            disabled={isRegenerating || disabled}
          >
            {isRegenerating ? (
              <>
                <SmallSpinner />
                <Typography variant="body-small">
                  {t("LLM_AI_FEEDBACK.GENERATE_DRAFT_GENERATING")}
                </Typography>
              </>
            ) : (
              <>
                <Icon.Add />
                <Typography variant="body-small">
                  {t("LLM_AI_FEEDBACK.GENERATE_DRAFT_BUTTON")}
                </Typography>
              </>
            )}
          </ActionButton>
        </ActionTooltip>
        <PillDivider />
        <Select
          value={language}
          disabled={isRegenerating || disabled}
          itemsPanelPlacement="bottom-end"
        >
          <LanguageTrigger>
            <LanguageLabel>
              <Flag market={language === "SV" ? "SE" : "GB"} />
            </LanguageLabel>
          </LanguageTrigger>
          <Select.Options>
            <Select.Option value="EN" onClick={() => setLanguage("EN")}>
              <LanguageLabel>
                <Flag market="GB" />
                {"EN"}
              </LanguageLabel>
            </Select.Option>
            <Select.Option value="SV" onClick={() => setLanguage("SV")}>
              <LanguageLabel>
                <Flag market="SE" />
                {"SV"}
              </LanguageLabel>
            </Select.Option>
          </Select.Options>
        </Select>
      </Pill>
      <ConfirmButtonWithDialog
        maxWidth={"412px"}
        open={showRegenerateConfirm}
        handleClose={handleRegenerateConfirm}
        title={t("LLM_AI_FEEDBACK.REGENERATE_CONFIRM_TITLE")}
        description={""}
        confirmText={t("LLM_AI_FEEDBACK.REGENERATE_CONFIRM_BUTTON")}
        cancelText={t("CANCEL")}
      >
        <Typography variant="body" color="GRAY_DARK">
          {t("LLM_AI_FEEDBACK.REGENERATE_CONFIRM_DESCRIPTION")}
        </Typography>
      </ConfirmButtonWithDialog>
    </DraftPanel>
  );
};
